import React, { useState } from "react";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Mail,
  Phone,
  BookOpen,
  Shield,
  Users,
  Settings,
  FileText,
  HelpCircle,
  X,
  Monitor,
  Clock,
  Award,
  Database,
  BarChart3,
  Lock,
  Layers,
  Coins
} from "lucide-react";
import { motion } from "framer-motion";
import LogoImage from "../../assets/MainImage/LogoCBT_1.png";
import { TermsConditionsModal } from "./TermsAndCondition";
import { PrivacyPolicyModal } from "./PrivacyPolicyModal";
import { LegalDisclaimerModal } from "./LegallDisclaimerModal";
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto  ">
      <div className="flex items-center justify-center min-h-screen   px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        <div className="inline-block w-[90%] mx-auto p-6 mt-[15vh]  mb-4 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const openModal = (modalType: string) => setActiveModal(modalType);
  const closeModal = () => setActiveModal(null);

  const featuresContent = (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center mb-3">
            <Layers className="text-blue-600 mr-2" size={20} />
            <h4 className="font-semibold text-gray-900">
              Dual Mode Examination
            </h4>
          </div>
          <p className="text-gray-600 text-sm">
            Conduct exams in both online and offline modes with seamless
            synchronization and security features.
          </p>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center mb-3">
            <Monitor className="text-blue-600 mr-2" size={20} />
            <h4 className="font-semibold text-gray-900">Online Proctoring</h4>
          </div>
          <p className="text-gray-600 text-sm">
            Advanced AI-powered proctoring with real-time monitoring, face
            detection, and browser lockdown features.
          </p>
        </div>

        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center mb-3">
            <BookOpen className="text-green-600 mr-2" size={20} />
            <h4 className="font-semibold text-gray-900">
              Flexible Exam Management
            </h4>
          </div>
          <p className="text-gray-600 text-sm">
            Configure exams with granular control over format, timing, and
            content. Multiple papers under an exam and Chapter/section wise
            question set
          </p>
        </div>

        <div className="p-4 bg-purple-50 rounded-lg">
          <div className="flex items-center mb-3">
            <Award className="text-purple-600 mr-2" size={20} />
            <h4 className="font-semibold text-gray-900">Result Processing</h4>
          </div>
          <p className="text-gray-600 text-sm">
            Streamline post-exam workflows with our comprehensive result
            management system.
          </p>
        </div>
        <div className="p-4 bg-purple-50 rounded-lg">
          <div className="flex items-center mb-3">
            <Users className="text-purple-600 mr-2" size={20} />
            <h4 className="font-semibold text-gray-900">Raise Objection</h4>
          </div>
          <p className="text-gray-600 text-sm">
            Ensure fairness with our structured review and objection handling
            system.
          </p>
        </div>

        <div className="p-4 bg-orange-50 rounded-lg">
          <div className="flex items-center mb-3">
            <Database className="text-orange-600 mr-2" size={20} />
            <h4 className="font-semibold text-gray-900">Question Bank</h4>
          </div>
          <p className="text-gray-600 text-sm">
            Comprehensive question library with multiple formats, difficulty
            levels, and subject categorization.
          </p>
        </div>

        <div className="p-4 bg-red-50 rounded-lg">
          <div className="flex items-center mb-3">
            <BarChart3 className="text-red-600 mr-2" size={20} />
            <h4 className="font-semibold text-gray-900">Analytics Dashboard</h4>
          </div>
          <p className="text-gray-600 text-sm">
            Detailed performance analytics with student progress tracking and
            institutional insights.
          </p>
        </div>

        <div className="p-4 bg-indigo-50 rounded-lg">
          <div className="flex items-center mb-3">
            <Lock className="text-indigo-600 mr-2" size={20} />
            <h4 className="font-semibold text-gray-900">Security Features</h4>
          </div>
          <p className="text-gray-600 text-sm">
            Multi-layered security with encryption, secure browsers, and
            plagiarism detection.
          </p>
        </div>
      </div>
    </div>
  );

  const documentationContent = (
    <div className="space-y-6">
      <div className="prose max-w-none">
        <h4 className="text-lg font-semibold mb-4">Getting Started Guide</h4>

        <div className="space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h5 className="font-medium">For Educational Institutions</h5>
            <ol className="list-decimal list-inside text-sm text-gray-600 mt-2 space-y-1">
              <li>
                Connect with our sales team to explore subscription options.
              </li>
              <li>
                Complete the onboarding process with assistance from our support
                team.
              </li>
              <li>
                Import or create question sets using Excel, including subject,
                mark allocation, and complexity levels.
              </li>
              <li>
                Register all candidates with valid profile pictures and
                signatures.
              </li>
              <li>Schedule and conduct your first examination.</li>
            </ol>
          </div>

          <div className="border-l-4 border-green-500 pl-4">
            <h5 className="font-medium">For Administrators</h5>
            <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
              <li>Manage users and assign roles efficiently.</li>
              <li>Configure and schedule examinations seamlessly.</li>
              <li>Set up proctoring features and enforce security policies.</li>
              <li>
                Generate detailed reports and access insightful analytics.
              </li>
              <li>Integrate with existing systems via APIs.</li>
            </ul>
          </div>

          <div className="border-l-4 border-purple-500 pl-4">
            <h5 className="font-medium">For Faculty Members</h5>
            <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
              <li>Create and manage comprehensive question banks.</li>
              <li>Design and organize exams and assessments.</li>
              <li>Track student progress in real time.</li>
              <li>
                Monitor candidate activity via the Central Monitoring Unit.
              </li>
            </ul>
          </div>

          <div className="border-l-4 border-orange-500 pl-4">
            <h5 className="font-medium">Technical Requirements</h5>
            <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
              <li>
                Use of a modern web browser (Chrome, Firefox, Safari, or Edge).
              </li>
              <li>
                Stable internet connection with a minimum speed of 2 Mbps.
              </li>
              <li>
                Functional webcam and microphone for proctored examinations.
              </li>
              <li>
                Updated operating system (Windows 10 or later, macOS 10.14 or
                later).
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const helpCenterContent = (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">Frequently Asked Questions</h4>

          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <h5 className="font-medium text-sm mb-1">
                How do I schedule an exam?
              </h5>
              <p className="text-xs text-gray-600">
                Navigate to the Exams section, click "Create New Exam", set your
                parameters, and publish when ready.
              </p>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <h5 className="font-medium text-sm mb-1">
                What browsers are supported?
              </h5>
              <p className="text-xs text-gray-600">
                We support all modern browsers. For proctored exams, we
                recommend using our secure browser plugin.
              </p>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <h5 className="font-medium text-sm mb-1">
                How is student data protected?
              </h5>
              <p className="text-xs text-gray-600">
                We use enterprise-grade encryption and comply with two way RSA +
                AES256 and other privacy regulations.
              </p>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <h5 className="font-medium text-sm mb-1">
                Is this solution suitable for private coaching institutions?
              </h5>
              <p className="text-xs text-gray-600">
                Yes, this platform is fully compatible with private coaching
                institutions and can be tailored to meet specific academic and
                administrative needs.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="text-lg font-semibold">Contact Support</h4>

          <div className="space-y-3">
            <div className="flex items-center p-3 bg-blue-50 rounded-lg">
              <Mail className="text-blue-600 mr-3" size={16} />
              <div>
                <p className="font-medium text-sm">Email Support</p>
                <p className="text-xs text-gray-600">support@msqube.com</p>
                <p className="text-xs text-gray-500">
                  Response within 24 hours
                </p>
              </div>
            </div>

            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <Phone className="text-green-600 mr-3" size={16} />
              <div>
                <p className="font-medium text-sm">Phone Support</p>
                <p className="text-xs text-gray-600">033-4814-4250</p>
                <p className="text-xs text-gray-500">
                  Mon-Fri, 9 AM - 6 PM IST
                </p>
              </div>
            </div>

            <div className="flex items-center p-3 bg-purple-50 rounded-lg">
              <WhatsAppIcon
                className="text-green-500"
                sx={{ fontSize: "19px", color: "#22c55e" }}
              />
              <div className="ml-3">
                <p className="font-medium text-sm">Whatsapp Support</p>
                <p className="text-xs text-gray-600">7044442848</p>
              </div>
            </div>
          </div>

          {/* <div className="mt-6 p-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg text-white">
            <h5 className="font-semibold mb-2">Need Priority Support?</h5>
            <p className="text-sm mb-3">
              Upgrade to our Premium plan for 24/7 dedicated support and faster
              response times.
            </p>
            <button className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">
              Learn More
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );

  const solutionsContent = (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow">
          <div className="">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <BookOpen className="text-blue-600" size={24} />
            </div>
            <h4 className="text-lg text-center font-semibold mb-2">
              K-12 Schools
            </h4>
            <p className="text-gray-600 text-sm mb-4">
              Comprehensive testing solutions for primary and secondary
              education with age-appropriate interfaces.
            </p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• Student-friendly interface</li>
              <li>• Parent progress reports</li>
              <li>• Curriculum alignment</li>
              <li>• Accessibility features</li>
            </ul>
          </div>
        </div>

        <div className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow">
          <div className="">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Users className="text-green-600" size={24} />
            </div>
            <h4 className="text-lg text-center font-semibold mb-2">
              Universities
            </h4>
            <p className="text-gray-600 text-sm mb-4">
              Advanced testing platform for higher education with research-grade
              analytics and security.
            </p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• Advanced proctoring</li>
              <li>• Research analytics</li>
              <li>• LMS integration</li>
              <li>• Scalable infrastructure</li>
            </ul>
          </div>
        </div>

        <div className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-shadow">
          <div className="">
            <div className="mx-auto w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Award className="text-purple-600" size={24} />
            </div>
            <h4 className="text-lg font-semibold mb-2 text-center">
              Certification Bodies
            </h4>
            <p className="text-gray-600 text-sm mb-4">
              Professional certification and licensing exams with
              industry-standard security protocols.
            </p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• Identity verification</li>
              <li>• Secure delivery</li>
              <li>• Compliance reporting</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-xl">
        <h4 className="text-xl font-semibold mb-4">
          Why Choose Our CBT Platform?
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-8">
          <div className="flex items-start">
            <Shield className="text-blue-600 mr-3 mt-1" size={20} />
            <div>
              <h5 className="font-medium">Enterprise Security</h5>
              <p className="text-sm text-gray-600">
                Bank-level encryption and compliance with international
                standards
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <Layers className="text-blue-600 mr-3 mt-1" size={20} />
            <div>
              <h5 className="font-medium">Dual Mode Examination</h5>
              <p className="text-sm text-gray-600">
                Conduct exams in both online and offline modes with seamless
                synchronization and security features.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <BookOpen className="text-green-600 mr-3 mt-1" size={20} />
            <div>
              <h5 className="font-medium">Flexible Exam Management</h5>
              <p className="text-sm text-gray-600">
                Configure exams with granular control over format, timing, and
                content.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <Coins className="text-blue-600 mr-3 mt-1" size={20} />
            <div>
              <h5 className="font-medium">Credit-Based Subscription Model</h5>
              <p className="text-sm text-gray-600">
                Pay-as-you-go with credits for flexible access. Top up credits
                to unlock all features and content as needed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const caseStudiesContent = (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold">Delhi Public School</h4>
              <p className="text-sm text-gray-600">
                K-12 Education • 5,000+ Students
              </p>
            </div>
            <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
              95% Efficiency Gain
            </div>
          </div>
          <p className="text-gray-700 mb-4">
            "Implementing QCBT transformed our examination process. We reduced
            exam administration time by 75% while maintaining the highest
            security standards. The AI proctoring gave us confidence in remote
            testing during the pandemic."
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">75%</div>
              <div className="text-xs text-gray-600">Time Saved</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">99.9%</div>
              <div className="text-xs text-gray-600">Uptime</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">5K+</div>
              <div className="text-xs text-gray-600">Students</div>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold">
                Indian Institute of Technology
              </h4>
              <p className="text-sm text-gray-600">
                Higher Education • 10,000+ Students
              </p>
            </div>
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
              Research Grade
            </div>
          </div>
          <p className="text-gray-700 mb-4">
            "The advanced analytics and customizable question types allowed us
            to conduct sophisticated assessments. The platform's scalability
            handled our entrance exams with over 100,000 concurrent users
            seamlessly."
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">100K+</div>
              <div className="text-xs text-gray-600">Concurrent Users</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">0%</div>
              <div className="text-xs text-gray-600">Downtime</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">15+</div>
              <div className="text-xs text-gray-600">Question Types</div>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold">
                National Certification Board
              </h4>
              <p className="text-sm text-gray-600">
                Professional Certification • 50,000+ Candidates
              </p>
            </div>
            <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
              Industry Standard
            </div>
          </div>
          <p className="text-gray-700 mb-4">
            "Security was our top priority for professional licensing exams.
            QCBT's multi-layered security approach and detailed audit trails
            gave us the confidence to digitize our entire certification
            process."
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600">100%</div>
              <div className="text-xs text-gray-600">Compliance</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">50K+</div>
              <div className="text-xs text-gray-600">Candidates</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">24/7</div>
              <div className="text-xs text-gray-600">Support</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-16 pb-8 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-indigo-600 rounded-full filter blur-3xl"></div>
        </div>

        <div className="w-[90%] mx-auto px-4 relative z-10">
          {/* Main footer grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Brand column */}
            <div className="">
              <div className="w-full h-1/2  overflow-hidden flex justify-center">
                <img
                  src={LogoImage}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mt-2 ml-10">
                The next-generation examination platform powered by AI and
                advanced proctoring technologies.
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-5">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Navigation
              </h3>
              <ul className="space-y-3">
                {[
                  { name: "Features", action: () => openModal("features") },
                  { name: "Solutions", action: () => openModal("solutions") },
                ].map((item, index) => (
                  <li key={index}>
                    <button
                      onClick={item.action}
                      className="text-gray-300 hover:text-white text-sm transition-colors flex items-center group"
                    >
                      <span className="w-1.5 h-1.5 bg-gray-500 rounded-full mr-2 group-hover:bg-blue-400 transition-colors"></span>
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div className="space-y-5">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                Resources
              </h3>
              <ul className="space-y-3">
                {[
                  {
                    name: "Documentation",
                    action: () => openModal("documentation"),
                  },

                  {
                    name: "Help Center",
                    action: () => openModal("helpCenter"),
                  },
                ].map((item, index) => (
                  <li key={index}>
                    <button
                      onClick={item.action}
                      className="text-gray-300 hover:text-white text-sm transition-colors flex items-center group"
                    >
                      <span className="w-1.5 h-1.5 bg-gray-500 rounded-full mr-2 group-hover:bg-indigo-400 transition-colors"></span>
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-5">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                Connect
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="p-1.5 bg-gray-700 rounded-lg mr-3">
                    <Mail className="text-blue-400 text-sm" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm mt-0.5">
                      cbt@msqube.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="p-1.5 bg-gray-700 rounded-lg mr-3">
                    <Phone className="text-blue-400 text-sm" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm mt-0.5">
                      033-4814-4250
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-700 my-8"></div>

          {/* Bottom footer */}
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded flex items-center justify-center">
                <Monitor className="text-white" size={12} />
              </div>
              <span className="text-gray-400 text-xs md:text-sm">
                © {new Date().getFullYear()} QCBT by{" "}
                <a
                  target="_blank"
                  href="https://msqube.com"
                  className="text-blue-400"
                >
                  MSQUBE Technology Solutions Pvt Ltd
                </a>
              </span>
            </div>

            <div className="flex space-x-6">
              <button
                onClick={() => openModal("privacy")}
                className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => openModal("terms")}
                className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors"
              >
                Terms & Conditions
              </button>
              <button
                onClick={() => openModal("legal")}
                className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors"
              >
                Legal Disclaimer
              </button>
            </div>
          </div>
        </div>
        <div className="mt-8">
          <div className="flex items-center justify-center">
            <span className="text-xs text-gray-400">
              Developed by{" "}
              <a
                target="_blank"
                href="https://msqube.com"
                className="text-blue-400"
              >
                MSQUBE Technology Solutions Pvt Ltd
              </a>
            </span>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <Modal
        isOpen={activeModal === "features"}
        onClose={closeModal}
        title="Platform Features"
      >
        {featuresContent}
      </Modal>

      <Modal
        isOpen={activeModal === "documentation"}
        onClose={closeModal}
        title="Documentation"
      >
        {documentationContent}
      </Modal>

      <Modal
        isOpen={activeModal === "helpCenter"}
        onClose={closeModal}
        title="Help Center"
      >
        {helpCenterContent}
      </Modal>

      <Modal
        isOpen={activeModal === "solutions"}
        onClose={closeModal}
        title="Our Solutions"
      >
        {solutionsContent}
      </Modal>

      <Modal
        isOpen={activeModal === "caseStudies"}
        onClose={closeModal}
        title="Case Studies"
      >
        {caseStudiesContent}
      </Modal>
      <PrivacyPolicyModal
        isOpen={activeModal === "privacy"}
        onClose={closeModal}
      />
      <TermsConditionsModal
        isOpen={activeModal === "terms"}
        onClose={closeModal}
      />
      <LegalDisclaimerModal
        isOpen={activeModal === "legal"}
        onClose={closeModal}
      />

      {/* <Modal
        isOpen={activeModal === "terms"}
        onClose={closeModal}
        title="Terms of Service"
      >
        
      </Modal> */}
    </>
  );
};

export default Footer;
